import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { AttachmentManager } from '@app/components/business/AttachmentManager';
import { ModalFormContract, ModalInjectedProps, ModalInstanceApi } from '@context/modal-provider';
import useCaptureScreen from '@hooks/useCaptureScreen';
import { Button, Input, message as antMessage } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ErrorMessage, Formik, FormikProps } from 'formik';
import type { UploadRequestOption as RcUploadRequestOption, UploadProgressEvent } from 'rc-upload/lib/interface';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RiScreenshot2Fill } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export interface IssueReportResult {
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  filename: string;
  fileBase64: string;
  attachments: File[];
}

const getPriorityOptions = (t: (key: string) => string) => [
  { value: 'high' as const, label: t('issue-reporting-priority-high'), color: '#fc8f8d' },
  { value: 'medium' as const, label: t('issue-reporting-priority-medium'), color: '#fab83e' },
  { value: 'low' as const, label: t('issue-reporting-priority-low'), color: '#47cf90' },
];

const getValidationSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    title: Yup.string().required(t('issue-reporting-validation-title-required')),
    description: Yup.string()
      .min(10, t('issue-reporting-validation-description-min'))
      .required(t('issue-reporting-validation-description-required')),
  });

export type IssueReportModalProps = ModalInjectedProps<IssueReportResult> &
  Partial<ModalFormContract<IssueReportResult, IssueReportResult>> & {
    formRef?: React.RefObject<FormikProps<IssueReportResult> | null>;
  };

const IssueReportModal: React.FC<IssueReportModalProps> = ({ resolve, close, onSubmit, formRef: externalFormRef }) => {
  const { t } = useTranslation();
  const priorityOptions = useMemo(() => getPriorityOptions(t), [t]);
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const { filename, screenshot, captureScreen, downloadScreen, resetCapture } = useCaptureScreen();
  const [attachments, setAttachments] = useState<Array<{ id: number; file: File }>>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<number, string | null>>({});
  const previewUrlsRef = useRef<Record<number, string | null>>({});

  const internalFormRef = useRef<FormikProps<IssueReportResult>>(null);
  const formRef = externalFormRef || internalFormRef;

  const revokePreviewUrl = useCallback(
    (id: number) => {
      const url = previewUrls[id];
      if (url) {
        URL.revokeObjectURL(url);
      }
      setPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [previewUrls]
  );

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleAttachmentUpload = useCallback(
    async (options: RcUploadRequestOption<void>) => {
      const { file, onError, onSuccess, onProgress } = options;
      try {
        const startProgress: UploadProgressEvent = { percent: 10 };
        onProgress?.(startProgress, file);

        const uploadFile = file as File;
        setAttachments((prev) => {
          const id = Date.now() + Math.floor(Math.random() * 1000);
          if (uploadFile.type?.startsWith('image/')) {
            const url = URL.createObjectURL(uploadFile);
            setPreviewUrls((existing) => ({ ...existing, [id]: url }));
          }
          return [...prev, { id, file: uploadFile }];
        });

        const endProgress: UploadProgressEvent = { percent: 100 };
        onProgress?.(endProgress, file);
        onSuccess?.(undefined, new XMLHttpRequest());
        antMessage.success(t('issue-reporting-attachment-added'));
      } catch (error) {
        onError?.(error as Error);
        antMessage.error(t('issue-reporting-attachment-add-failed'));
      }
    },
    [t]
  );

  const handleAttachmentDelete = useCallback(
    (attachment: { attachmentId: number; filename: string }) => {
      setAttachments((prev) => {
        const target = prev.find((item) => item.id === attachment.attachmentId);
        if (target) {
          revokePreviewUrl(target.id);
        }
        return prev.filter((item) => item.id !== attachment.attachmentId);
      });
      antMessage.success(t('issue-reporting-attachment-removed'));
    },
    [revokePreviewUrl, t]
  );

  const handleAttachmentDownload = useCallback(
    (attachment: { attachmentId: number; filename: string }) => {
      const entry = attachments.find((f) => f.id === attachment.attachmentId);
      if (entry) {
        const url = URL.createObjectURL(entry.file);
        const link = document.createElement('a');
        link.href = url;
        link.download = entry.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
    [attachments]
  );

  const ensurePreviewUrl = useCallback(
    async (attachment: { attachmentId: number; contentType?: string | null }) => {
      const existing = previewUrls[attachment.attachmentId];
      if (existing) return existing;

      if (!attachment.contentType?.startsWith('image/')) {
        setPreviewUrls((prev) => ({ ...prev, [attachment.attachmentId]: null }));
        return null;
      }

      const entry = attachments.find((item) => item.id === attachment.attachmentId);
      if (entry) {
        const url = URL.createObjectURL(entry.file);
        setPreviewUrls((prev) => ({ ...prev, [attachment.attachmentId]: url }));
        return url;
      }

      return null;
    },
    [attachments, previewUrls]
  );

  const submitStep = async (values: IssueReportResult) => {
    try {
      const result: IssueReportResult = {
        ...values,
        priority,
        attachments: attachments.map((item) => item.file),
      };

      if (onSubmit) {
        const api: ModalInstanceApi<IssueReportResult> = {
          id: 'issue-report',
          close: () => close(),
          resolve: (res?: IssueReportResult) => resolve(res),
          reject: (err?: unknown) => console.error(err),
          setOkLoading: () => {},
          setCancelLoading: () => {},
        };

        await onSubmit(result, api);
      } else {
        resolve(result);
      }

      setPriority('medium');
      attachments.forEach((attachment) => revokePreviewUrl(attachment.id));
      setAttachments([]);
      setPreviewUrls({});
    } catch (err) {
      console.error('Could not create issue report.', err);
      if (onSubmit) {
        const api: ModalInstanceApi<IssueReportResult> = {
          id: 'issue-report',
          close: () => close(),
          resolve: (res?: IssueReportResult) => resolve(res),
          reject: (err?: unknown) => console.error(err),
          setOkLoading: () => {},
          setCancelLoading: () => {},
        };
        api.reject(err);
      }
    }
    close();
  };

  return (
    <Formik
      innerRef={formRef}
      validationSchema={validationSchema}
      initialValues={{
        title: '',
        description: '',
        priority: 'medium' as 'high' | 'medium' | 'low',
        filename: '',
        fileBase64: '',
        attachments: [],
      }}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        await submitStep(values);
        resetForm();
        resetCapture();
        attachments.forEach((attachment) => revokePreviewUrl(attachment.id));
        setAttachments([]);
        setPreviewUrls({});
      }}
    >
      {({ values, setFieldValue, handleSubmit, errors, touched, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col justify-center gap-4">
            <div className="flex gap-2 flex-col">
              <div>
                <label className="form-label">{t('issue-reporting-field-title')}</label>
              </div>
              <div className="flex flex-col gap-2">
                <Input
                  name="title"
                  placeholder={t('issue-reporting-field-title-placeholder')}
                  value={values.title}
                  onChange={(e) => setFieldValue('title', e.target.value)}
                  status={errors.title && touched.title ? 'error' : undefined}
                />
                <ErrorMessage name="title">
                  {(msg) => <div style={{ color: 'red', fontSize: '12px' }}>{msg}</div>}
                </ErrorMessage>
              </div>
            </div>

            <div className="flex gap-2 flex-col">
              <div>
                <label className="form-label">{t('issue-reporting-field-description-label')}</label>
              </div>
              <TextArea
                rows={4}
                placeholder={t('issue-reporting-field-description-placeholder')}
                onChange={(e) => setFieldValue('description', e.target.value)}
                value={values.description}
                status={errors.description && touched.description ? 'error' : undefined}
              />
              <ErrorMessage name="description">
                {(msg) => <div style={{ color: 'red', fontSize: '12px' }}>{msg}</div>}
              </ErrorMessage>
            </div>

            <div className="flex gap-2 flex-col">
              <div>
                <label className="form-label">{t('issue-reporting-field-priority')}</label>
              </div>
              <div className="flex flex-row gap-2 flex-wrap">
                {priorityOptions.map((option) => (
                  <div key={`priority-${option.value}`}>
                    <Button
                      style={{
                        border: `1px solid ${option.color}`,
                        backgroundColor: priority === option.value ? option.color : 'transparent',
                        color: priority === option.value ? '#fff' : option.color,
                      }}
                      onClick={() => setPriority(option.value)}
                    >
                      {option.label}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 flex-col">
              <label className="form-label">{t('issue-reporting-field-screenshot')}</label>
              <div className="flex flex-row gap-2 flex-wrap">
                <Button
                  icon={<RiScreenshot2Fill />}
                  onClick={() =>
                    captureScreen({
                      ignoreIds: ['bugReport'],
                      ignoreClasses: ['ant-modal-root'],
                    }).then((x) => {
                      if (x) {
                        setFieldValue('fileBase64', x.base64);
                        setFieldValue('filename', x.filename);
                      }
                    })
                  }
                >
                  {t('issue-reporting-btn-take-screenshot')}
                </Button>

                {filename && (
                  <>
                    <Button icon={<DownloadOutlined />} onClick={() => downloadScreen()}>
                      {t('issue-reporting-btn-download')}
                    </Button>

                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => {
                        resetCapture();
                        setFieldValue('filename', '');
                        setFieldValue('fileBase64', '');
                      }}
                    >
                      {t('issue-reporting-btn-clear-screenshot')}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {filename && screenshot && (
              <div className="group relative rounded shadow z-0 max-h-96 overflow-auto">
                <img
                  className="w-full z-10 object-cover"
                  src={`data:image/png;base64,${screenshot}`}
                  alt="Screenshot"
                />
              </div>
            )}

            <div className="flex gap-2 flex-col">
              <label className="form-label">{t('issue-reporting-field-attachments')}</label>
              <AttachmentManager
                attachments={attachments.map((item) => ({
                  attachmentId: item.id,
                  filename: item.file.name,
                  contentType: item.file.type,
                  uploadedAt: new Date().toISOString(),
                }))}
                onDownload={handleAttachmentDownload}
                onDelete={handleAttachmentDelete}
                onUpload={handleAttachmentUpload}
                previewUrls={previewUrls}
                ensurePreviewUrl={ensurePreviewUrl}
                deletingAttachmentId={null}
                inModal={true}
              />
            </div>

            {/* Submit button with loading state */}
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={close}>{t('common-cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                {t('issue-reporting-btn-submit')}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default IssueReportModal;
